import {
  type HandoffResult,
  isHandoffUrl,
  readHandoffResult,
  toAuthError,
} from '@/auth/handoff-url';

export const POPUP_MESSAGE_SOURCE = 'questionnaire-assistant/sso';

/** Set before `window.open`; read by the popup through its inherited storage copy. */
export const POPUP_FLAG_KEY = 'sso_popup_pending';

export type PopupMessage = HandoffResult & {
  source: typeof POPUP_MESSAGE_SOURCE;
};

export type PopupLoginResult =
  | HandoffResult
  /** Closed without finishing. Not an error. */
  | { status: 'cancelled' }
  /** `window.open` refused; the caller falls back to a full-page redirect. */
  | { status: 'blocked' };

const POPUP_WIDTH = 520;
const POPUP_HEIGHT = 640;
const POLL_MS = 250;
/**
 * A popup closes itself in the same task that posts its result, so `closed` can
 * flip before the message arrives. Wait this long before calling it a cancel.
 */
const CLOSE_GRACE_MS = 250;

function popupFeatures(): string {
  // Centre on the current screen rather than the primary one.
  const left = window.screenX + (window.outerWidth - POPUP_WIDTH) / 2;
  const top = window.screenY + (window.outerHeight - POPUP_HEIGHT) / 2;
  return [
    'popup=yes',
    `width=${POPUP_WIDTH}`,
    `height=${POPUP_HEIGHT}`,
    `left=${Math.max(0, Math.round(left))}`,
    `top=${Math.max(0, Math.round(top))}`,
  ].join(',');
}

function setPopupFlag(value: boolean): void {
  try {
    if (value) {
      sessionStorage.setItem(POPUP_FLAG_KEY, '1');
    } else {
      sessionStorage.removeItem(POPUP_FLAG_KEY);
    }
  } catch {
    // Privacy modes can refuse storage; the location poll still completes the flow.
  }
}

function parseMessage(data: unknown): HandoffResult | undefined {
  if (typeof data !== 'object' || data === null) {
    return undefined;
  }
  const message = data as Record<string, unknown>;
  if (message.source !== POPUP_MESSAGE_SOURCE) {
    return undefined;
  }
  if (message.status === 'success' && typeof message.token === 'string') {
    return { status: 'success', token: message.token };
  }
  if (message.status === 'error') {
    return { status: 'error', reason: toAuthError(message.reason) };
  }
  return undefined;
}

/** Throws while the popup is cross-origin; only the final hop home is readable. */
function readPopupLocation(popup: Window): HandoffResult | undefined {
  let href: string;
  try {
    href = popup.location.href;
  } catch {
    return undefined;
  }
  if (!href || href === 'about:blank') {
    return undefined;
  }

  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return undefined;
  }
  if (url.origin !== window.location.origin || !isHandoffUrl(url)) {
    return undefined;
  }
  return readHandoffResult(url);
}

/**
 * Must be reached synchronously from the click handler — a `window.open` after
 * an `await` is treated as unsolicited and blocked. `async` is safe: the body
 * runs to the first `return` in the click's own task.
 */
export async function loginWithPopup(
  authorizeUrl: string
): Promise<PopupLoginResult> {
  setPopupFlag(true);

  let popup: Window | null;
  try {
    popup = window.open(
      authorizeUrl,
      'questionnaire-assistant-sso',
      popupFeatures()
    );
  } catch {
    setPopupFlag(false);
    return { status: 'blocked' };
  }

  if (!popup || popup.closed) {
    setPopupFlag(false);
    return { status: 'blocked' };
  }

  return new Promise<PopupLoginResult>((resolve) => {
    let settled = false;
    let poll = 0;
    let closeTimer: ReturnType<typeof setTimeout> | undefined;

    const finish = (result: PopupLoginResult): void => {
      if (settled) {
        return;
      }
      settled = true;
      window.clearInterval(poll);
      if (closeTimer !== undefined) {
        clearTimeout(closeTimer);
      }
      window.removeEventListener('message', onMessage);
      setPopupFlag(false);
      try {
        if (!popup.closed) {
          popup.close();
        }
      } catch {
        // Already gone; nothing to clean up.
      }
      resolve(result);
    };

    function onMessage(event: MessageEvent): void {
      if (event.origin !== window.location.origin || event.source !== popup) {
        return;
      }
      const result = parseMessage(event.data);
      if (result) {
        finish(result);
      }
    }

    window.addEventListener('message', onMessage);

    poll = window.setInterval(() => {
      const result = readPopupLocation(popup);
      if (result) {
        finish(result);
        return;
      }

      // Closed without a result: the user dismissed the popup.
      if (popup.closed && closeTimer === undefined) {
        closeTimer = setTimeout(() => {
          finish({ status: 'cancelled' });
        }, CLOSE_GRACE_MS);
      }
    }, POLL_MS);
  });
}
